import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { seo, seoLinks } from "~/utils/seo";
import { HeroDarkSplit } from "~/components/blocks/HeroDarkSplit";

/**
 * /press-kit — C1 brand assets download page.
 *
 * Hidden from the primary nav and mega-menu (it's a code-only route, not
 * registered in navigation config, and is excluded from the sitemap's static
 * page list). The only entry point is the "Press Kit" link under the Company
 * column in the footer.
 *
 * Header layout matches the Partners page: the `/press-kit` path is mapped to
 * the `dark` header mode in Header.tsx, and the hero reuses the same
 * `HeroDarkSplit` block the Partners page uses.
 *
 * Layout structurally references linear.app/brand: a single left-aligned
 * content column with a global "Download brand assets" button in the hero,
 * followed by stacked asset sections (preview panel → per-asset downloads),
 * a color palette of copyable swatches, and text usage guidelines. All visual
 * styling uses C1 fonts, components, and brand colors only.
 */

const ASSETS_ZIP = "/downloads/c1-brand-assets.zip";

export const Route = createFileRoute("/press-kit")({
  head: () => ({
    meta: seo({
      title: "Press Kit",
      description:
        "Download official C1 brand assets — logos, logomarks, and color palette — and learn how to use them correctly.",
      url: "/press-kit",
    }),
    links: seoLinks({ url: "/press-kit" }),
  }),
  loader: () => ({}),
  component: Brand,
});

/* ------------------------------------------------------------------ */
/*  Trigger one or more file downloads from a single click.            */
/*                                                                     */
/*  Each asset is fetched as a Blob and saved via an object-URL anchor.*/
/*  Object URLs with a `download` attribute always save the file and   */
/*  never navigate the page — this avoids the bug where a delayed,     */
/*  user-activation-expired anchor click would follow its href and     */
/*  scroll the page back to the top instead of downloading.            */
/* ------------------------------------------------------------------ */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

async function downloadFiles(files: { href: string; name: string }[]) {
  if (typeof document === "undefined") return;
  for (const { href, name } of files) {
    try {
      const res = await fetch(href);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      saveBlob(await res.blob(), name);
    } catch {
      // Fallback: direct same-origin download link.
      const a = document.createElement("a");
      a.href = href;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
}

const DOWNLOAD_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 rounded-lg border border-teal-dark px-4 py-1.5 font-body text-13 font-semibold text-teal-dark transition-colors duration-300 hover:bg-teal-dark hover:text-white";

/* ------------------------------------------------------------------ */
/*  Asset section — preview panel + per-asset download button          */
/* ------------------------------------------------------------------ */
function AssetSection({
  heading,
  description,
  variants,
}: {
  heading: string;
  description: string;
  variants: {
    label: string;
    src: string;
    download: string;
    /** matching PNG file for the same asset */
    srcPng: string;
    downloadPng: string;
    /**
     * Optional bundle holding this variant's SVG + PNG. When set, the button
     * becomes a plain download link to the archive instead of firing two
     * separate saves.
     */
    zip?: { href: string; download: string };
    /** preview panel background */
    panel: "light" | "dark";
    /** logomark renders smaller than the full lockup */
    size: "logo" | "mark";
  }[];
}) {
  return (
    <section className="border-t border-[#DCDADA] py-12 lg:py-16">
      <h2 className="font-display text-28 font-bold text-charcoal md:text-33">
        {heading}
      </h2>
      <p className="mt-3 max-w-2xl font-body text-base leading-relaxed text-gray-80 md:text-19">
        {description}
      </p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        {variants.map((v) => (
          <div key={v.label} className="flex flex-col">
            <div
              className={`flex flex-1 items-center justify-center rounded-20 border p-12 ${
                v.panel === "dark"
                  ? "border-transparent bg-[#161415]"
                  : "border-[#DCDADA] bg-cream"
              }`}
            >
              <img
                src={v.src}
                alt={`C1 ${v.label}`}
                className={v.size === "mark" ? "h-16 w-auto" : "h-9 w-auto"}
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="font-body text-13 font-semibold text-gray-80">
                {v.label}
              </span>
              {v.zip ? (
                <a
                  href={v.zip.href}
                  download={v.zip.download}
                  className={DOWNLOAD_BUTTON_CLASS}
                >
                  <DownloadIcon />
                  <span>Download</span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    downloadFiles([
                      { href: v.src, name: v.download },
                      { href: v.srcPng, name: v.downloadPng },
                    ])
                  }
                  className={DOWNLOAD_BUTTON_CLASS}
                >
                  <DownloadIcon />
                  <span>Download</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Color swatch — click to copy hex                                   */
/* ------------------------------------------------------------------ */
function ColorSwatch({ name, hex }: { name: string; hex: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(hex).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="group flex flex-col overflow-hidden rounded-20 border border-[#DCDADA] bg-white text-left transition-shadow hover:shadow-md"
      aria-label={`Copy ${name} hex ${hex}`}
    >
      <span
        className="h-24 w-full"
        style={{ backgroundColor: hex }}
        aria-hidden="true"
      />
      <span className="flex items-center justify-between px-4 py-3">
        <span>
          <span className="block font-body text-sm font-semibold text-charcoal">
            {name}
          </span>
          <span className="block font-mono text-13 uppercase text-gray-60">
            {hex}
          </span>
        </span>
        <span className="font-body text-11 font-semibold uppercase tracking-wide text-gray-60 group-hover:text-teal-dark">
          {copied ? "Copied" : "Copy"}
        </span>
      </span>
    </button>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1v9m0 0L4.5 6.5M8 10l3.5-3.5M2 13h12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const BRAND_COLORS = [
  { name: "Dark Teal", hex: "#00343A" },
  { name: "Electric Pink", hex: "#FF92EB" },
  { name: "Dark Grey", hex: "#231F20" },
  { name: "Brand White", hex: "#F7F7F5" },
];

const USAGE_RULES = [
  "Maintain clear space around the logo equal to the height of the logomark on all sides.",
  "Use the white logo on dark backgrounds and the dark logo on light backgrounds to preserve contrast.",
  "Do not stretch, distort, rotate, recolor, or add effects such as shadows or outlines to the logo.",
  "You may not incorporate these graphics into the name of your own business, product, or service.",
  "Don’t combine the C1.ai graphics with any other graphics without written consent from C1.ai.",
];

function Brand() {
  return (
    <div>
      {/* Hero — same layout as the Partners page (HeroDarkSplit, dark header) */}
      <HeroDarkSplit
        superheading="Press Kit"
        heading="C1.ai brand assets"
        copy="Everything you need to represent C1.ai accurately and consistently — logos, logomarks, and our color palette. Download the full kit or grab individual files below."
        background_image="/images/partners-hero-bg.webp"
        button_primary={{ text: "Download brand assets", url: ASSETS_ZIP }}
        button_primary_variant="white"
        button_primary_class="!text-teal-dark"
      />

      {/* Single left-aligned content column */}
      <div className="px-4 md:px-8">
        <div className="mx-auto max-w-screen-lg">
          {/* We are C1 */}
          <section className="py-12 lg:py-16">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-28 font-bold text-charcoal md:text-33">
                  We are C1.ai
                </h2>
                <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-gray-80 md:text-19">
                  Caring deeply about our customers — and how quickly we can make
                  a positive impact on their lives — has been a core company value
                  since ConductorOne was founded. As we grow, so does our mission.
                  We don't just conduct power; we've evolved into an AI-native
                  constant energy source that flows towards our customers. Our logo
                  reflects this: an ever-flowing power circuit.
                </p>
              </div>
              <div className="overflow-hidden rounded-20 bg-cream">
                <video
                  src="/images/bg-vid-light-1.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="C1 ever-flowing power circuit animation"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </div>
          </section>

          <AssetSection
            heading="Logo"
            description="The primary C1.ai logo combines the logomark and wordmark. It's the most recognizable expression of the brand — use it wherever space allows."
            variants={[
              {
                label: "Dark — for light backgrounds",
                src: "/images/svg/c1-ai-logo-dark.svg",
                download: "C1.ai-Logo.svg",
                srcPng: "/images/svg/c1-ai-logo-dark.png",
                downloadPng: "C1.ai-Logo.png",
                zip: {
                  href: "/downloads/c1-ai-logo-dark.zip",
                  download: "C1.ai-Logo.zip",
                },
                panel: "light",
                size: "logo",
              },
              {
                label: "White — for dark backgrounds",
                src: "/images/svg/c1-ai-logo-white.svg",
                download: "C1.ai-Logo-White.svg",
                srcPng: "/images/svg/c1-ai-logo-white.png",
                downloadPng: "C1.ai-Logo-White.png",
                zip: {
                  href: "/downloads/c1-ai-logo-white.zip",
                  download: "C1.ai-Logo-White.zip",
                },
                panel: "dark",
                size: "logo",
              },
            ]}
          />

          <AssetSection
            heading="Logomark"
            description="The C1.ai logomark stands on its own where the full logo won't fit — app icons, avatars, favicons, and other tight spaces."
            variants={[
              {
                label: "Dark — for light backgrounds",
                src: "/images/svg/c1-mark-dark.svg",
                download: "Mark Dark.svg",
                srcPng: "/images/svg/c1-mark-dark.png",
                downloadPng: "Mark Dark.png",
                panel: "light",
                size: "mark",
              },
              {
                label: "Light — for dark backgrounds",
                src: "/images/svg/c1-mark-light.svg",
                download: "Mark_Light.svg",
                srcPng: "/images/svg/c1-mark-light.png",
                downloadPng: "Mark_Light.png",
                panel: "dark",
                size: "mark",
              },
            ]}
          />

          {/* Colors */}
          <section className="border-t border-[#DCDADA] py-12 lg:py-16">
            <h2 className="font-display text-28 font-bold text-charcoal md:text-33">
              Colors
            </h2>
            <p className="mt-3 max-w-2xl font-body text-base leading-relaxed text-gray-80 md:text-19">
              Our core palette. Click any swatch to copy its hex value.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {BRAND_COLORS.map((c) => (
                <ColorSwatch key={c.hex} name={c.name} hex={c.hex} />
              ))}
            </div>
          </section>

          {/* Usage guidelines */}
          <section className="border-t border-[#DCDADA] py-12 lg:py-16">
            <h2 className="font-display text-28 font-bold text-charcoal md:text-33">
              Usage guidelines
            </h2>
            <p className="mt-3 max-w-2xl font-body text-base leading-relaxed text-gray-80 md:text-19">
              Please follow these guidelines to keep the C1.ai brand consistent and
              recognizable.
            </p>
            <ul className="mt-6 max-w-2xl space-y-3">
              {USAGE_RULES.map((rule) => (
                <li key={rule} className="flex gap-3">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-dark"
                    aria-hidden="true"
                  />
                  <span className="font-body text-base leading-relaxed text-gray-80">
                    {rule}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-8 font-body text-base text-gray-80">
              Questions about brand usage? Email{" "}
              <a
                href="mailto:claire.mckenna@c1.ai"
                className="font-semibold text-teal-dark underline underline-offset-2"
              >
                claire.mckenna@c1.ai
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
